import {
  BatteryVendor,
  BuildingOccupancyType,
  ChargeStrategy,
  CoStarPropertyType,
  CoStarSecondaryType,
  DischargeStrategy,
  DoeReferenceType,
  FinProductType,
  GuardrailScope,
  IrrType,
  LeaseType,
  LoadCategory,
  LoadOrigin,
  MaxPvOrigin,
  MeterSource,
  Metering,
  Month,
  PVoptGoal,
  PolicyReason,
  ProductionOrigin,
  PvType,
  SiteActionItemType,
  SiteStatus,
  SolarLocation,
  StatusReason,
  TariffOrigin,
  UsState,
} from "@/v2/graphql/admin-generated-rq"
import {
  DataSourceLabel,
  DocumentTag,
  EnergyThing,
  FinmodelInputOverrideSource,
  FormCategory,
  FormState,
  IntervalTimeZone,
  IntervalUnits,
  MeteringOrUnsure,
  OfferStatus,
  OfferUnavailableReason,
  PpaOfftaker,
  PpaProductType,
  YesNoUnsure,
} from "@/v2/graphql/public-generated-rq"

export const LoadCategoryNames = {
  [LoadCategory.CommercialTenants]: "Commercial",
  [LoadCategory.MfCombined]: "Multifamily: House + Tenants",
  [LoadCategory.MfHouse]: "Multifamily: House",
  [LoadCategory.MfTenants]: "Multifamily: Tenants",
} as const

export const LoadOriginNames = {
  [LoadOrigin.StockEstimate]: "Estimate",
  [LoadOrigin.ScaledAnnually]: "Estimate (scaled to annual load)",
  [LoadOrigin.ScaledMonthly]: "Estimate (scaled to monthly bills)",
  [LoadOrigin.MeterIntervals]: "Intervals",
  [LoadOrigin.Merge]: "Merged",
} as const

export const LoadOriginDescriptions = {
  [LoadOrigin.StockEstimate]:
    "We estimated the electricity usage of this property based on similar buildings.",
  [LoadOrigin.ScaledAnnually]:
    "We scaled our electricty usage estimate to match real annual usage.",
  [LoadOrigin.ScaledMonthly]:
    "We scaled our electricty usage estimate to real monthly usage.",

  [LoadOrigin.MeterIntervals]:
    "This meter uses real electricity usage data from your property.",
  [LoadOrigin.Merge]: "This meter combines real and estimated data.",
} as const

export const SolarLocationNames = {
  [SolarLocation.RoofOnly]: "Roof Only",
  [SolarLocation.CarportOnly]: "Carport Only",
  [SolarLocation.RoofAndCarport]: "Roof & Carport",
  [SolarLocation.Ground]: "Ground",
} as const

export const DoeReferenceTypeNames = {
  [DoeReferenceType.Office]: "Office",
  [DoeReferenceType.Multifamily]: "Multi-Family",
  [DoeReferenceType.Hotel]: "Hotel",
  [DoeReferenceType.Hospital]: "Hospital",
  [DoeReferenceType.Outpatient]: "Outpatient",
  [DoeReferenceType.RetailStandalone]: "Retail, Standalone",
  [DoeReferenceType.RetailStripMall]: "Retail, Strip Mall",
  [DoeReferenceType.QuickServiceRestaurant]: "Quick Service Restaurant",
  [DoeReferenceType.FullServiceRestaurant]: "Full Service Restaurant",
  [DoeReferenceType.PrimarySchool]: "Primary School",
  [DoeReferenceType.SecondarySchool]: "Secondary School",
  [DoeReferenceType.SingleFamilyDetached]: "Single Family Detached",
  [DoeReferenceType.Warehouse]: "Warehouse",
} as const

export const SiteStatusNames = {
  [SiteStatus.Active]: "Active",
  [SiteStatus.Archived]: "Archived",
  [SiteStatus.OnHold]: "On hold",
} as const

export const SiteStatusReasonNames = {
  [StatusReason.InsufficientFinancialReturn]: "Insufficient financial return",
  [StatusReason.RoofNeedsReplacing]: "Roof needs replacement",
  [StatusReason.BuildingWillBeSoldSoon]: "Building will be sold soon",
  [StatusReason.SystemSizeTooSmall]: "System size too small",
  [StatusReason.SolarInstalled]: "Solar installed",
  [StatusReason.HighRiseWithBusyRoof]: "High-rise with busy roof",
  [StatusReason.Other]: "Other",
} as const

export const Meterings = {
  [Metering.MasterMetered]: "Master metered",
  [Metering.SubMetered]: "Sub-metered",
  [Metering.UtilityMetered]: "Utility metered",
  [Metering.UtilityMeteredCommonsRubs]: "Utility metered (Common Area RUBS)",
} as const

export const LeaseTypes = {
  [LeaseType.GrossLease]: "Gross Lease",
  [LeaseType.NetLease]: "Net Lease",
  [LeaseType.NnLease]: "Double Net Lease (NN)",
  [LeaseType.NnnLease]: "Triple Net Lease (NNN)",
} as const

export const BuildingOccupancyTypes = {
  [BuildingOccupancyType.OwnerOccupied]: "Owner occupied",
  [BuildingOccupancyType.SingleThirdPartyTenant]: "Single third party tenant",
  [BuildingOccupancyType.MultipleThirdPartyTenants]: "Multiple third party tenants",
} as const

export const MeteringOrUnsureLabels = {
  ...Meterings,
  [MeteringOrUnsure.MasterMetered]: "Master metered",
  [MeteringOrUnsure.SubMetered]: "Sub-metered",
  [MeteringOrUnsure.UtilityMetered]: "Utility metered",
  [MeteringOrUnsure.UtilityMeteredCommonsRubs]: "Utility metered (Common Area RUBS)",
  [MeteringOrUnsure.Unsure]: "Not sure",
} as const

export const MonthToNum = {
  [Month.January]: 1,
  [Month.February]: 2,
  [Month.March]: 3,
  [Month.April]: 4,
  [Month.May]: 5,
  [Month.June]: 6,
  [Month.July]: 7,
  [Month.August]: 8,
  [Month.September]: 9,
  [Month.October]: 10,
  [Month.November]: 11,
  [Month.December]: 12,
} as const

export const UsStateNames = {
  [UsState.Al]: "Alabama",
  [UsState.Ak]: "Alaska",
  [UsState.Az]: "Arizona",
  [UsState.Ar]: "Arkansas",
  [UsState.Ca]: "California",
  [UsState.Co]: "Colorado",
  [UsState.Ct]: "Connecticut",
  [UsState.De]: "Delaware",
  [UsState.Dc]: "District of Columbia",
  [UsState.Fl]: "Florida",
  [UsState.Ga]: "Georgia",
  [UsState.Hi]: "Hawaii",
  [UsState.Id]: "Idaho",
  [UsState.Il]: "Illinois",
  [UsState.In]: "Indiana",
  [UsState.Ia]: "Iowa",
  [UsState.Ks]: "Kansas",
  [UsState.Ky]: "Kentucky",
  [UsState.La]: "Louisiana",
  [UsState.Me]: "Maine",
  [UsState.Md]: "Maryland",
  [UsState.Ma]: "Massachusetts",
  [UsState.Mi]: "Michigan",
  [UsState.Mn]: "Minnesota",
  [UsState.Ms]: "Mississippi",
  [UsState.Mo]: "Missouri",
  [UsState.Mt]: "Montana",
  [UsState.Ne]: "Nebraska",
  [UsState.Nv]: "Nevada",
  [UsState.Nh]: "New Hampshire",
  [UsState.Nj]: "New Jersey",
  [UsState.Nm]: "New Mexico",
  [UsState.Ny]: "New York",
  [UsState.Nc]: "North Carolina",
  [UsState.Nd]: "North Dakota",
  [UsState.Oh]: "Ohio",
  [UsState.Ok]: "Oklahoma",
  [UsState.Or]: "Oregon",
  [UsState.Pa]: "Pennsylvania",
  [UsState.Pr]: "Puerto Rico",
  [UsState.Ri]: "Rhode Island",
  [UsState.Sc]: "South Carolina",
  [UsState.Sd]: "South Dakota",
  [UsState.Tn]: "Tennessee",
  [UsState.Tx]: "Texas",
  [UsState.Ut]: "Utah",
  [UsState.Vt]: "Vermont",
  [UsState.Va]: "Virginia",
  [UsState.Wa]: "Washington",
  [UsState.Wv]: "West Virginia",
  [UsState.Wi]: "Wisconsin",
  [UsState.Wy]: "Wyoming",
} as const

export const MeterSourceNames = {
  [MeterSource.Aquicore]: "Aquicore",
  [MeterSource.Comstock]: "ComStock",
  [MeterSource.Resstock]: "ResStock",
  [MeterSource.Upload]: "Upload",
  [MeterSource.UtilityApi]: "UtilityAPI",
  [MeterSource.Bill]: "Bill",
  [MeterSource.Merge]: "Merged",
} as const

export const DataSourceLabelNames = {
  [DataSourceLabel.Estimated]: "Estimated",
  [DataSourceLabel.Verified]: "Verified",
  [DataSourceLabel.PartiallyVerified]: "Partially verified",
} as const

export const CoStarPropertyTypeNames = {
  [CoStarPropertyType.Flex]: "Flex",
  [CoStarPropertyType.HealthCare]: "Health Care",
  [CoStarPropertyType.Hospitality]: "Hospitality",
  [CoStarPropertyType.Industrial]: "Industrial",
  [CoStarPropertyType.Land]: "Land",
  [CoStarPropertyType.Multifamily]: "Multifamily",
  [CoStarPropertyType.Office]: "Office",
  [CoStarPropertyType.Retail]: "Retail",
  [CoStarPropertyType.ShoppingCenter]: "Shopping Center",
  [CoStarPropertyType.Specialty]: "Specialty",
  [CoStarPropertyType.SportsEntertainment]: "Sports & Entertainment",
  [CoStarPropertyType.Student]: "Student",
  [CoStarPropertyType.SingleFamily]: "Single Family",
}

export const CoStarSecondaryTypeNames = {
  [CoStarSecondaryType.AirplaneHangar]: "Airplane Hangar",
  [CoStarSecondaryType.Airport]: "Airport",
  [CoStarSecondaryType.AmusementPark]: "Amusement Park",
  [CoStarSecondaryType.Apartments]: "Apartments",
  [CoStarSecondaryType.AssistedLiving]: "Assisted Living",
  [CoStarSecondaryType.AutoDealership]: "Auto Dealership",
  [CoStarSecondaryType.AutoRepair]: "Auto Repair",
  [CoStarSecondaryType.AutoSalvageFacility]: "Auto Salvage Facility",
  [CoStarSecondaryType.Bank]: "Bank",
  [CoStarSecondaryType.Bar]: "Bar",
  [CoStarSecondaryType.BaseballField]: "Baseball Field",
  [CoStarSecondaryType.BowlingAlley]: "Bowling Alley",
  [CoStarSecondaryType.CarWash]: "Car Wash",
  [CoStarSecondaryType.Casino]: "Casino",
  [CoStarSecondaryType.CementGravelPlant]: "Cement Gravel Plant",
  [CoStarSecondaryType.CemeteryMausoleum]: "Cemetery Mausoleum",
  [CoStarSecondaryType.ChemicalOilRefinery]: "Chemical Oil Refinery",
  [CoStarSecondaryType.Commercial]: "Commercial",
  [CoStarSecondaryType.CongregateSeniorHousing]: "Congregate Senior Housing",
  [CoStarSecondaryType.ContinuingCareRetirementCommunity]:
    "Continuing Care Retirement Community",
  [CoStarSecondaryType.ContractorStorageYard]: "Contractor Storage Yard",
  [CoStarSecondaryType.ConvenienceStore]: "Convenience Store",
  [CoStarSecondaryType.CorrectionalFacility]: "Correctional Facility",
  [CoStarSecondaryType.DayCareCenter]: "Day Care Center",
  [CoStarSecondaryType.DepartmentStore]: "Department Store",
  [CoStarSecondaryType.Distribution]: "Distribution",
  [CoStarSecondaryType.Dormitory]: "Dormitory",
  [CoStarSecondaryType.DriveInMovie]: "Drive-in Movie",
  [CoStarSecondaryType.DrugStore]: "Drug Store",
  [CoStarSecondaryType.FastFood]: "Fast Food",
  [CoStarSecondaryType.Flex]: "Flex",
  [CoStarSecondaryType.FoodProcessing]: "Food Processing",
  [CoStarSecondaryType.Freestanding]: "Freestanding",
  [CoStarSecondaryType.FuneralHome]: "Funeral Home",
  [CoStarSecondaryType.GardenCenter]: "Garden Center",
  [CoStarSecondaryType.GeneralRetail]: "General Retail",
  [CoStarSecondaryType.GolfCourseDrivingRange]: "Golf Course/Driving Range",
  [CoStarSecondaryType.HealthCare]: "Health Care",
  [CoStarSecondaryType.HealthClub]: "Health Club",
  [CoStarSecondaryType.HorseStables]: "Horse Stables",
  [CoStarSecondaryType.Hospital]: "Hospital",
  [CoStarSecondaryType.Hospitality]: "Hospitality",
  [CoStarSecondaryType.Hotel]: "Hotel",
  [CoStarSecondaryType.HotelCasino]: "Hotel Casino",
  [CoStarSecondaryType.Industrial]: "Industrial",
  [CoStarSecondaryType.IndustrialLiveWorkUnit]: "Industrial Live/Work Unit",
  [CoStarSecondaryType.Land]: "Land",
  [CoStarSecondaryType.Landfill]: "Landfill",
  [CoStarSecondaryType.LightDistribution]: "Light Distribution",
  [CoStarSecondaryType.LightManufacturing]: "Light Manufacturing",
  [CoStarSecondaryType.LodgeMeetingHall]: "Lodge/Meeting Hall",
  [CoStarSecondaryType.LoftCreativeSpace]: "Loft/Creative Space",
  [CoStarSecondaryType.Lumberyard]: "Lumberyard",
  [CoStarSecondaryType.ManufacturedHousingMobileHomePark]:
    "Manufactured Housing/Mobile Home Park",
  [CoStarSecondaryType.Manufacturing]: "Manufacturing",
  [CoStarSecondaryType.Marina]: "Marina",
  [CoStarSecondaryType.Medical]: "Medical",
  [CoStarSecondaryType.Motel]: "Motel",
  [CoStarSecondaryType.MovieRadioTvStudio]: "Movie/Radio/TV Studio",
  [CoStarSecondaryType.MovieTheatre]: "Movie Theatre",
  [CoStarSecondaryType.Multifamily]: "Multifamily",
  [CoStarSecondaryType.OfficeBuilding]: "Office Building",
  [CoStarSecondaryType.OfficeLiveWorkUnit]: "Office Live/Work Unit",
  [CoStarSecondaryType.OfficeResidential]: "Office/Residential",
  [CoStarSecondaryType.ParkingGarage]: "Parking Garage",
  [CoStarSecondaryType.ParkingLot]: "Parking Lot",
  [CoStarSecondaryType.PoliceFireStation]: "Police / Fire Station",
  [CoStarSecondaryType.PostOffice]: "Post Office",
  [CoStarSecondaryType.PublicLibrary]: "Public Library",
  [CoStarSecondaryType.RD]: "R&D",
  [CoStarSecondaryType.RaceTrack]: "Race Track",
  [CoStarSecondaryType.RadioTvTransmissionFacilities]:
    "Radio/TV Transmission Facilities",
  [CoStarSecondaryType.RailroadYard]: "Railroad Yard",
  [CoStarSecondaryType.RecyclingCenter]: "Recycling Center",
  [CoStarSecondaryType.RefrigerationColdStorage]: "Refrigeration/Cold Storage",
  [CoStarSecondaryType.RehabilitationCenter]: "Rehabilitation Center",
  [CoStarSecondaryType.ReligiousFacility]: "Religious Facility",
  [CoStarSecondaryType.Residential]: "Residential",
  [CoStarSecondaryType.ResidentialIncome]: "Residential Income",
  [CoStarSecondaryType.Restaurant]: "Restaurant",
  [CoStarSecondaryType.Retail]: "Retail",
  [CoStarSecondaryType.RetailBuilding]: "Retail Building",
  [CoStarSecondaryType.Schools]: "Schools",
  [CoStarSecondaryType.SelfStorage]: "Self-Storage",
  [CoStarSecondaryType.Service]: "Service",
  [CoStarSecondaryType.ServiceStation]: "Service Station",
  [CoStarSecondaryType.Shelter]: "Shelter",
  [CoStarSecondaryType.Shipyard]: "Shipyard",
  [CoStarSecondaryType.Showroom]: "Showroom",
  [CoStarSecondaryType.SingleRoomOccupancyHotel]: "Single Room Occupancy Hotel",
  [CoStarSecondaryType.SkatingRink]: "Skating Rink",
  [CoStarSecondaryType.SkilledNursingFacility]: "Skilled Nursing Facility",
  [CoStarSecondaryType.SororityFraternityHouse]: "Sorority / Fraternity House",
  [CoStarSecondaryType.Specialty]: "Specialty",
  [CoStarSecondaryType.SportsEntertainment]: "Sports & Entertainment",
  [CoStarSecondaryType.Storefront]: "Storefront",
  [CoStarSecondaryType.StorefrontRetailOffice]: "Storefront Retail/Office",
  [CoStarSecondaryType.StorefrontRetailResidential]: "Storefront Retail/Residential",
  [CoStarSecondaryType.Supermarket]: "Supermarket",
  [CoStarSecondaryType.SwimmingPool]: "Swimming Pool",
  [CoStarSecondaryType.TelecomHotelDataHosting]: "Telecom Hotel/Data Hosting",
  [CoStarSecondaryType.TheaterConcertHall]: "Theater/Concert Hall",
  [CoStarSecondaryType.TrailerCamperPark]: "Trailer / Camper Park",
  [CoStarSecondaryType.TruckStop]: "Truck Stop",
  [CoStarSecondaryType.TruckTerminal]: "Truck Terminal",
  [CoStarSecondaryType.Unknown]: "Unknown",
  [CoStarSecondaryType.UtilitySubstation]: "Utility Substation",
  [CoStarSecondaryType.VeterinarianKennel]: "Veterinarian Kennel",
  [CoStarSecondaryType.Warehouse]: "Warehouse",
  [CoStarSecondaryType.WaterRetentionFacility]: "Water Retention Facility",
  [CoStarSecondaryType.WaterTreatmentFacility]: "Water Treatment Facility",
  [CoStarSecondaryType.WineryVineyard]: "Winery/Vineyard",
  [CoStarSecondaryType.Agricultural]: "Agricultural",
  [CoStarSecondaryType.ApartmentsStudent]: "Apartments (Student)",
  [CoStarSecondaryType.BarNightclub]: "Bar/Nightclub",
  [CoStarSecondaryType.BedAndBreakfast]: "Bed and Breakfast",
  [CoStarSecondaryType.DormitoryStudent]: "Dormitory (Student)",
  [CoStarSecondaryType.Hostel]: "Hostel",
  [CoStarSecondaryType.ManufacturedHousingMobileHomeParkStudent]:
    "Manufactured Housing/Mobile Home Park (Student)",
  [CoStarSecondaryType.Quickservice]: "Quickservice",
  [CoStarSecondaryType.ServicedApartment]: "Serviced Apartment",
} as const

export const MaxPVOriginNames = {
  [MaxPvOrigin.Default]: "Default (Missing Data for Max Estimate)",
  [MaxPvOrigin.Estimate]: "Estimated Physical Max",
  [MaxPvOrigin.Kwh]: "90% of Annual kWh",
  [MaxPvOrigin.Site]: "Site Physical Max",
} as const

export const PVoptGoalNames = {
  [PVoptGoal.MaxNpv]: "Max Investor NPV",
  [PVoptGoal.MaxCashPurchaseNpv]: "Max Cash NPV",
  [PVoptGoal.ForceKw]: "Force kW",
}

// Disallow selection of deprecated PVopt goals.
export const LuxGoalNames = {
  [PVoptGoal.MaxCashPurchaseNpv]: "Max Cash NPV",
  [PVoptGoal.ForceKw]: "Force kW",
}

export const ProductionOriginNames = {
  [ProductionOrigin.Helioscope]: "Helioscope",
  [ProductionOrigin.Pvwatts]: "PVWatts",
  [ProductionOrigin.Bid]: "Bid",
} as const

export const IntervalTimeZoneLabels = {
  [IntervalTimeZone.Local]: "Building timezone",
  [IntervalTimeZone.Utc]: "UTC",
  [IntervalTimeZone.Unsure]: "Not sure",
} as const

export const IntervalUnitsLabels = {
  [IntervalUnits.Kw]: "kW",
  [IntervalUnits.Kwh]: "kWh",
  [IntervalUnits.Unsure]: "Not sure",
} as const

export const OfferStatusLabels = {
  [OfferStatus.Unavailable]: "No offer available",
  [OfferStatus.PpaContract]: "PPA contract",
  [OfferStatus.TermSheet]: "Term sheet",
  [OfferStatus.Verified]: "Verified offer",
  [OfferStatus.Final]: "Final offer",
  [OfferStatus.Estimated]: "Estimated offer",
} as const

export const OfferUnavailableReasonLabels = {
  [OfferUnavailableReason.NoPpaProduct]:
    "This property did not meet eligibility for any PPA product",
  [OfferUnavailableReason.InsufficientYearOneVnemProfit]: "No VNEM profit in year 1",
  [OfferUnavailableReason.NegativeSavingsInAnyYear]:
    "Our analysis predicts at least one year of negative savings",
  [OfferUnavailableReason.InvalidRoofRent]:
    "This property does not support roof rent to the building owner",
} as const

export const PPAProductTypeLabels = {
  [PpaProductType.OwnerPpa]: "Owner PPA",
  [PpaProductType.OwnerPpaWithVnemMf]: "Owner PPA with VNEM (MF)",
  [PpaProductType.OwnerPpaWithVnemNonMf]: "Owner PPA with VNEM (Non-MF)",
  [PpaProductType.TenantPpaWithSiteLease]: "Tenant PPA with site lease",
} as const

export const TariffOriginNames = {
  [TariffOrigin.Aquicore]: "Aquicore",
  [TariffOrigin.UtilityApi]: "UtilityAPI",
  [TariffOrigin.User]: "User",
  [TariffOrigin.StockEstimate]: "Estimate",
}

export const IrrTypeNames = {
  [IrrType.PreTaxIrr]: "Pre-Tax IRR (pre_tax_irr)",
  [IrrType.PreTaxItcIrr]: "Pre-Tax ITC IRR (pre_tax_itc_irr)",
  [IrrType.PostTaxItcDeprIrr]: "Post-Tax IRR (post_tax_itc_depr_irr)",
}

export const FormCategoryLabels = {
  [FormCategory.EnergyData]: "Energy data form",
  [FormCategory.Evaluation]: "Property evaluation",
  [FormCategory.Qualification]: "Qualification form",
}

export const FormStatusNames = {
  [FormState.Draft]: "In Progress",
  [FormState.Final]: "Submitted",
  [FormState.New]: "Not started",
  [FormState.Trash]: "Deleted",
} as const

export const SiteActionItemTypeLabels = {
  [SiteActionItemType.HighIrr]: "Post-Tax IRR > 30%",
  [SiteActionItemType.HighCostPerKwh]: "$/kWh > $0.30",
}

export const FinmodelInputOverrideSourceLabels = {
  [FinmodelInputOverrideSource.Site]: "Property",
  [FinmodelInputOverrideSource.Portfolio]: "Portfolio",
  [FinmodelInputOverrideSource.FinancingOrg]: "Organization",
  [FinmodelInputOverrideSource.Finproduct]: "Financing Product",
}

export const PVTypeNames = {
  [PvType.Roof]: "Roof",
  [PvType.CarportThreeHigh]: "Carport 3-high",
  [PvType.CarportSixHigh]: "Carport 6-high",
  [PvType.Ground]: "Ground",
}

export const BatteryVendorNames = {
  [BatteryVendor.Byd]: "BYD",
  [BatteryVendor.Socomec]: "Socomec",
  [BatteryVendor.Tesla]: "Tesla",
}

export const ChargeStrategyNames = {
  [ChargeStrategy.ExportAvoidance]: "Export Avoidance",
  [ChargeStrategy.LowestTou]: "Lowest ToU",
}

export const DischargeStrategyNames = {
  [DischargeStrategy.PeakTou]: "Peak ToU",
}

export const YesNoUnsureLabels = {
  [YesNoUnsure.Yes]: "Yes",
  [YesNoUnsure.No]: "No",
  [YesNoUnsure.Unsure]: "Unsure",
}

export const PpaOfftakerLabels = {
  [PpaOfftaker.PropertyOwner]: "Property owner",
  [PpaOfftaker.Tenant]: "Tenant",
}

export const AllowableSolarLocationLabels = {
  BUILDING_ROOF: "Building roof",
  PARKING_GARAGE_CARPORTS: "Parking garage roof (carports)",
  OUTDOOR_PARKING_CARPORTS: "Outdoor parking (carports)",
  OTHER: "Other",
} as const

export const GuardrailScopeLabels = {
  [GuardrailScope.Portfolio]: "Portfolio",
  [GuardrailScope.Site]: "Site",
  [GuardrailScope.Scenario]: "Scenario",
}

export const FinProductTypeLabels = {
  [FinProductType.Self]: "Cash",
  [FinProductType.Loan]: "Loan",
  [FinProductType.Ppa]: "PPA",
  [FinProductType.Lease]: "Lease",
  [FinProductType.Cs]: "Community solar",
}

export const EnergyThingLabels = {
  [EnergyThing.CarportSolar]: "Carport solar",
  [EnergyThing.GroundmountSolar]: "Groundmount solar",
  [EnergyThing.RooftopSolar]: "Rooftop solar",
  [EnergyThing.Storage]: "Battery storage",
}

export const PolicyReasonLabels = {
  [PolicyReason.PpasNotAllowed]: "PPAs not allowed",
  [PolicyReason.PpaStatusUnknown]: "PPA status is unknown or unclear",
  [PolicyReason.CsNotAllowed]: "Community solar not allowed",
  [PolicyReason.CsNotFeasible]: "Community solar is not feasible",
  [PolicyReason.CsStatusUnknown]: "Community solar status is unknown or unclear",
}

export const DocumentTagLabels = {
  [DocumentTag.CreditReport]: "Credit report",
  [DocumentTag.EpcBuildQuote]: "EPC/Build quote",
  [DocumentTag.EpcContract]: "EPC/Contract",
  [DocumentTag.EpcCosts]: "EPC/Costs",
  [DocumentTag.EpcDesign]: "EPC/Design",
  [DocumentTag.EpcEmr]: "EPC/EMR",
  [DocumentTag.EpcFinancial]: "EPC/Financials",
  [DocumentTag.EpcLicense]: "EPC/License",
  [DocumentTag.EpcOther]: "EPC/Other",
  [DocumentTag.EpcProduction]: "EPC/Production",
  [DocumentTag.EpcSchedule]: "EPC/Schedule",
  [DocumentTag.Incentives]: "Incentives",
  [DocumentTag.InterconnectionApplication]: "Interconnection/Application",
  [DocumentTag.InterconnectionOther]: "Interconnection/Other",
  [DocumentTag.InterconnectionResult]: "Interconnection/Result",
  [DocumentTag.PermitApplication]: "Permit/Application",
  [DocumentTag.PermitOther]: "Permit/Other",
  [DocumentTag.PermitResult]: "Permit/Result",
  [DocumentTag.PpaContract]: "PPA/Contract",
  [DocumentTag.PpaOther]: "PPA/Other",
  [DocumentTag.PpaTermSheet]: "PPA/Term sheet",
  [DocumentTag.Production_8760]: "Production/8760",
  [DocumentTag.ProductionHelioscope]: "Production/Helioscope",
  [DocumentTag.ProductionOther]: "Production/Other",
  [DocumentTag.ProductionWeather]: "Production/Weather",
  [DocumentTag.SiteBlueprint]: "Property/Blueprint",
  [DocumentTag.SiteEnergy]: "Property/Energy",
  [DocumentTag.SiteOther]: "Property/Other",
} as const
